import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Hash, Send, MessageSquare, ThumbsUp, X, User, Mail, Trash2, Edit2 } from 'lucide-react';
import { useAuth, API, getAuthHeaders } from '@/App';
import { toast } from 'sonner';
import { AVATAR_OPTIONS, EMOJI_AVATARS } from './ProfilePage';

// Avatar component for anonymous users - only color avatars
function AnonymousAvatar({ avatar, name, size = 'md' }) {
  const sizeClass = size === 'lg' ? 'w-10 h-10 text-base' : size === 'md' ? 'w-9 h-9 text-sm' : 'w-7 h-7 text-xs';
  const avatarData = avatar ? AVATAR_OPTIONS.find(a => a.id === avatar) : null;
  
  if (!avatar || !avatarData) {
    return (
      <div className={`${sizeClass} rounded-full bg-purple-500 flex items-center justify-center text-white font-bold shrink-0`}>
        {name?.charAt(0)?.toUpperCase() || 'A'}
      </div>
    );
  }

  return (
    <div 
      className={`${sizeClass} rounded-full flex items-center justify-center font-bold text-white shrink-0`}
      style={{ background: avatarData.bg }}
    >
      {name?.charAt(0)?.toUpperCase() || 'A'}
    </div>
  );
}

// Simple Reply component - no recursion
function SingleReply({ reply, parentId, depth, onLikeReply, onDeleteReply, onToggleReply, activeReplyId, user, navigate }) {
  const [showNested, setShowNested] = useState(false);
  const isAnonymous = reply.anonymous;
  const isEmojiAvatar = !isAnonymous && reply.user_profile_avatar && EMOJI_AVATARS?.includes(reply.user_profile_avatar);
  const isOwner = user?.user_id === reply.user_id;
  const userLiked = user && reply.liked_by && reply.liked_by.includes(user.user_id);
  const nestedReplies = reply.replies || [];
  const canMessage = user && reply.user_id !== user.user_id;
  
  return (
    <div className={`py-2 ${depth > 0 ? 'ml-4 pl-3 border-l-2 border-indigo-100' : 'border-t border-gray-200/50 first:border-t-0'}`}>
      <div className="flex items-start gap-2 group">
        {isAnonymous ? (
          <AnonymousAvatar avatar={reply.user_avatar} name={reply.user_name} size="sm" />
        ) : (
          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isEmojiAvatar ? 'bg-gray-100 text-base' : 'bg-indigo-500 text-white text-xs font-bold'}`}>
            {isEmojiAvatar ? reply.user_profile_avatar : reply.user_name?.charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <span className={`text-xs font-semibold ${isAnonymous ? 'text-purple-700' : 'text-gray-900'}`}>
            {reply.user_name}
          </span>
          <p className="text-xs text-gray-700 break-words">{reply.content}</p>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <button 
              onClick={() => onLikeReply(parentId, reply.message_id)}
              className={`flex items-center gap-1 text-xs transition-colors ${userLiked ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
            >
              <ThumbsUp className={`w-3 h-3 ${userLiked ? 'fill-current' : ''}`} />
              <span>{reply.likes || 0}</span>
            </button>
            {depth < 5 && (
              <button 
                onClick={() => onToggleReply(reply.message_id)}
                className={`flex items-center gap-1 text-xs transition-colors ${activeReplyId === reply.message_id ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'}`}
              >
                <MessageSquare className="w-3 h-3" />
                <span>Reply</span>
              </button>
            )}
            {canMessage && (
              <button 
                onClick={() => navigate(`/messages?user=${reply.user_id}&name=${encodeURIComponent(reply.user_name)}`)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 transition-colors"
              >
                <Mail className="w-3 h-3" />
                <span>Message</span>
              </button>
            )}
            {isOwner && (
              <button 
                onClick={() => window.confirm('Delete?') && onDeleteReply(parentId, reply.message_id)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-3 h-3" />
                <span>Delete</span>
              </button>
            )}
            {nestedReplies.length > 0 && (
              <button onClick={() => setShowNested(!showNested)} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                {showNested ? 'Hide' : 'Show'} {nestedReplies.length} {nestedReplies.length === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Show nested replies - rendered as flat list with indent */}
      {showNested && nestedReplies.length > 0 && (
        <NestedRepliesFlat replies={nestedReplies} parentId={reply.message_id} depth={depth + 1} onLikeReply={onLikeReply} onDeleteReply={onDeleteReply} onToggleReply={onToggleReply} activeReplyId={activeReplyId} user={user} navigate={navigate} />
      )}
    </div>
  );
}

// Flat nested replies - avoids recursion by rendering all levels at once - Memoized for performance
const NestedRepliesFlat = React.memo(function NestedRepliesFlat({ replies, parentId, depth, onLikeReply, onDeleteReply, onToggleReply, activeReplyId, user, navigate }) {
  // Flatten all nested replies into a single array with depth info
  const flatReplies = useMemo(() => {
    const flattenReplies = (items, d) => {
      let result = [];
      for (const item of items) {
        result.push({ ...item, _depth: d, _parentId: parentId });
        if (item.replies && item.replies.length > 0 && d < 5) {
          result = result.concat(flattenReplies(item.replies, d + 1));
        }
      }
      return result;
    };
    return flattenReplies(replies, depth);
  }, [replies, depth, parentId]);
  
  return (
    <div className="mt-1">
      {flatReplies.map((reply, i) => {
        const isAnonymous = reply.anonymous;
        const isEmojiAvatar = !isAnonymous && reply.user_profile_avatar && EMOJI_AVATARS?.includes(reply.user_profile_avatar);
        const isOwner = user?.user_id === reply.user_id;
        const userLiked = user && reply.liked_by && reply.liked_by.includes(user.user_id);
        const canMessage = user && reply.user_id !== user.user_id;
        const indent = reply._depth * 16;
        
        return (
          <div key={reply.message_id || i} className="py-2 border-t border-gray-200/50 dark:border-[#2a2a2a]" style={{ marginLeft: `${indent}px` }}>
            <div className="flex items-start gap-2 group pl-3 border-l-2 border-indigo-100 dark:border-purple-500/30">
              {isAnonymous ? (
                <AnonymousAvatar avatar={reply.user_avatar} name={reply.user_name} size="sm" />
              ) : (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isEmojiAvatar ? 'bg-gray-100 dark:bg-[#1a1a1a] text-base' : 'bg-indigo-500 text-white text-xs font-bold'}`}>
                  {isEmojiAvatar ? reply.user_profile_avatar : reply.user_name?.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className={`text-xs font-semibold ${isAnonymous ? 'text-purple-700 dark:text-purple-400' : 'text-gray-900 dark:text-white'}`}>{reply.user_name}</span>
                <p className="text-xs text-gray-700 dark:text-gray-300 break-words whitespace-pre-wrap">{reply.content}</p>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <button onClick={() => onLikeReply(parentId, reply.message_id)} className={`flex items-center gap-1 text-xs transition-colors ${userLiked ? 'text-indigo-600 dark:text-purple-400' : 'text-gray-500 hover:text-indigo-600 dark:hover:text-purple-400'}`}>
                    <ThumbsUp className={`w-3 h-3 ${userLiked ? 'fill-current' : ''}`} /><span>{reply.likes || 0}</span>
                  </button>
                  {reply._depth < 5 && (
                    <button onClick={() => onToggleReply(reply.message_id)} className={`flex items-center gap-1 text-xs transition-colors ${activeReplyId === reply.message_id ? 'text-indigo-600 dark:text-purple-400' : 'text-gray-500 hover:text-indigo-600 dark:hover:text-purple-400'}`}>
                      <MessageSquare className="w-3 h-3" /><span>Reply</span>
                    </button>
                  )}
                  {canMessage && (
                    <button onClick={() => navigate(`/messages?user=${reply.user_id}&name=${encodeURIComponent(reply.user_name)}`)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 dark:hover:text-purple-400 transition-colors">
                      <Mail className="w-3 h-3" /><span>Message</span>
                    </button>
                  )}
                  {isOwner && (
                    <button onClick={() => window.confirm('Delete?') && onDeleteReply(parentId, reply.message_id)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 className="w-3 h-3" /><span>Delete</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

// Replies container with reply input - Memoized for performance
const RepliesList = React.memo(function RepliesList({ replies, postId, onLikeReply, onDeleteReply, onReplyToReply, user, groupId, navigate }) {
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(() => localStorage.getItem('uniconnect_reply_anonymous') === 'true');

  const handleToggleReply = useCallback((replyId) => {
    setActiveReplyId(prev => prev === replyId ? null : replyId);
    setReplyContent('');
  }, []);

  const handleSubmitReply = async () => {
    if (!replyContent.trim() || isReplying || !activeReplyId) return;
    if (!user) { navigate('/login?mode=signup'); return; }
    setIsReplying(true);
    const success = await onReplyToReply(activeReplyId, replyContent.trim(), isAnonymous);
    setIsReplying(false);
    if (success) { setReplyContent(''); setActiveReplyId(null); }
  };

  return (
    <div className="mt-3 pl-12 bg-white dark:bg-[#141414] rounded-lg border border-gray-100 dark:border-[#2a2a2a] p-2 max-h-[500px] overflow-y-auto">
      {replies.map((reply, i) => (
        <SingleReply
          key={reply.message_id || i}
          reply={reply}
          parentId={postId}
          depth={0}
          onLikeReply={onLikeReply}
          onDeleteReply={onDeleteReply}
          onToggleReply={handleToggleReply}
          activeReplyId={activeReplyId}
          user={user}
          navigate={navigate}
        />
      ))}
      {activeReplyId && (
        <div className="mt-2 p-2 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-[#2a2a2a]">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Replying to comment...</div>
          <div className="flex gap-2">
            <Textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmitReply())}
              placeholder="Write a reply..."
              className="flex-1 min-h-[32px] max-h-[100px] text-xs bg-white dark:bg-[#0a0a0a] border-gray-200 dark:border-[#2a2a2a] resize-none"
              rows={1}
            />
            <Button onClick={handleSubmitReply} disabled={!replyContent.trim() || isReplying} size="sm" className="h-8 px-2 bg-indigo-600 hover:bg-indigo-700 self-end">
              <Send className="w-3 h-3" />
            </Button>
            <Button onClick={() => setActiveReplyId(null)} size="sm" variant="ghost" className="h-8 w-8 p-0 self-end">
              <X className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Switch checked={isAnonymous} onCheckedChange={(c) => { setIsAnonymous(c); localStorage.setItem('uniconnect_reply_anonymous', c.toString()); }} className="data-[state=checked]:bg-indigo-600 scale-75" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400">Anonymous</span>
          </div>
        </div>
      )}
    </div>
  );
});

function DiscussionPost({ post, onLike, userLiked, onReply, user, onMessage, navigate, onDelete, onEdit, onLikeReply, onDeleteReply, onReplyToReply, groupId }) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  // Persistent anonymous reply toggle - stored in localStorage
  const [isReplyAnonymous, setIsReplyAnonymous] = useState(() => {
    const saved = localStorage.getItem('uniconnect_reply_anonymous');
    return saved === 'true';
  });
  const isAnonymous = post.anonymous;
  const isOwner = user?.user_id === post.user_id;

  // Save anonymous preference when it changes
  const handleAnonymousToggle = (checked) => {
    setIsReplyAnonymous(checked);
    localStorage.setItem('uniconnect_reply_anonymous', checked.toString());
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim() || isReplying) return;
    
    setIsReplying(true);
    const success = await onReply(post.message_id, replyContent.trim(), isReplyAnonymous);
    setIsReplying(false);
    
    if (success) {
      setReplyContent('');
      setShowReplyInput(false);
      setShowReplies(true);
    }
  };

  const handleDelete = async () => {
    if (!isOwner || isDeleting) return;
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    setIsDeleting(true);
    await onDelete(post.message_id);
    setIsDeleting(false);
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    const success = await onEdit(post.message_id, editContent);
    if (success) {
      setIsEditing(false);
    }
  };

  const isUserLikedReply = (reply) => {
    if (!user) return false;
    return reply.liked_by && reply.liked_by.includes(user.user_id);
  };

  const repliesCount = post.replies_count || post.replies?.length || 0;
  const isEmojiAvatar = !isAnonymous && post.user_profile_avatar && EMOJI_AVATARS?.includes(post.user_profile_avatar);

  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200/50 p-3 hover:border-indigo-200 transition-all">
      <div className="flex items-start gap-3 mb-3">
        {isAnonymous ? (
          <AnonymousAvatar avatar={post.user_avatar} name={post.user_name} size="md" />
        ) : (
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isEmojiAvatar ? 'bg-gray-100 text-xl' : 'bg-indigo-500 text-white text-sm font-bold'}`}>
            {isEmojiAvatar ? post.user_profile_avatar : post.user_name?.charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-bold ${isAnonymous ? 'text-purple-700' : 'text-gray-900'}`}>
            {post.user_name}
          </span>
          {isEditing ? (
            <div className="mt-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full text-sm min-h-[80px]"
                placeholder="Edit your message..."
              />
              <div className="flex gap-2 mt-2">
                <Button onClick={handleEdit} size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs">Save</Button>
                <Button onClick={() => { setIsEditing(false); setEditContent(post.content); }} size="sm" variant="ghost" className="h-7 text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed break-words mt-1 whitespace-pre-wrap">{post.content}</p>
          )}
        </div>
      </div>
      
      <div className="flex flex-wrap items-center gap-3 pl-12">
        <button 
          data-testid={`like-btn-${post.message_id}`}
          onClick={() => onLike(post.message_id)}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
            userLiked ? 'text-indigo-600' : 'text-gray-600 hover:text-indigo-600'
          }`}
        >
          <ThumbsUp className={`w-4 h-4 ${userLiked ? 'fill-current' : ''}`} />
          <span>{post.likes || 0}</span>
        </button>
        <button 
          data-testid={`reply-btn-${post.message_id}`}
          onClick={() => setShowReplyInput(!showReplyInput)}
          className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-indigo-600 font-medium transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Reply {repliesCount > 0 && `(${repliesCount})`}</span>
        </button>
        {post.user_id !== user?.user_id && (
          <button 
            data-testid={`message-btn-${post.message_id}`}
            onClick={() => navigate(`/messages?user=${post.user_id}&name=${encodeURIComponent(post.user_name)}`)}
            className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-indigo-600 font-medium transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span>Message</span>
          </button>
        )}
        {/* Edit button - only for message owner */}
        {isOwner && !isEditing && (
          <button 
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-indigo-600 font-medium transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
        {/* Delete button - only for message owner */}
        {isOwner && (
          <button 
            data-testid={`delete-btn-${post.message_id}`}
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
          </button>
        )}
        {repliesCount > 0 && !showReplies && (
          <button 
            onClick={() => setShowReplies(true)}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Show replies
          </button>
        )}
        {showReplies && repliesCount > 0 && (
          <button 
            onClick={() => setShowReplies(false)}
            className="text-xs text-gray-500 hover:text-gray-700 font-medium"
          >
            Hide replies
          </button>
        )}
      </div>

      {/* Reply Input */}
      {showReplyInput && (
        <div className="mt-3 pl-12">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Textarea
                data-testid={`reply-input-${post.message_id}`}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSubmitReply())}
                placeholder="Write a reply..."
                className="flex-1 min-h-[36px] max-h-[120px] text-sm bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a] rounded-lg resize-none overflow-y-auto"
                rows={1}
              />
              <Button
                data-testid={`submit-reply-btn-${post.message_id}`}
                onClick={handleSubmitReply}
                disabled={!replyContent.trim() || isReplying}
                size="sm"
                className="h-9 px-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg self-end"
              >
                <Send className="w-3 h-3" />
              </Button>
              <Button
                onClick={() => { setShowReplyInput(false); setReplyContent(''); }}
                size="sm"
                variant="ghost"
                className="h-9 w-9 p-0 self-end"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id={`anonymous-reply-${post.message_id}`}
                checked={isReplyAnonymous}
                onCheckedChange={handleAnonymousToggle}
                className="data-[state=checked]:bg-indigo-600 scale-75"
              />
              <Label htmlFor={`anonymous-reply-${post.message_id}`} className="text-xs text-gray-500 dark:text-gray-400">
                Anonymous
              </Label>
            </div>
          </div>
        </div>
      )}

      {/* Replies List */}
      {showReplies && post.replies && post.replies.length > 0 && (
        <RepliesList
          replies={post.replies}
          postId={post.message_id}
          onLikeReply={onLikeReply}
          onDeleteReply={onDeleteReply}
          onReplyToReply={onReplyToReply}
          user={user}
          groupId={groupId}
          navigate={navigate}
        />
      )}
    </div>
  );
}

export default function CommunityPage({ onMarkRead }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState(() => {
    // Load draft from localStorage on mount
    const draft = localStorage.getItem(`community_draft_${groupId}`);
    return draft || '';
  });
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  // Save draft to localStorage when newPost changes
  useEffect(() => {
    if (newPost.trim()) {
      localStorage.setItem(`community_draft_${groupId}`, newPost);
    } else {
      localStorage.removeItem(`community_draft_${groupId}`);
    }
  }, [newPost, groupId]);

  // Load draft when groupId changes
  useEffect(() => {
    const draft = localStorage.getItem(`community_draft_${groupId}`);
    setNewPost(draft || '');
  }, [groupId]);

  // Mark community as read when entering (only for logged in users)
  const markAsRead = useCallback(async () => {
    if (!user) return; // Skip for guests
    try {
      await fetch(`${API}/communities/${groupId}/read`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      if (onMarkRead) {
        onMarkRead(groupId);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }, [groupId, onMarkRead, user]);

  // Fetch community details (no auth needed)
  const fetchCommunity = useCallback(async () => {
    try {
      const response = await fetch(`${API}/communities/${groupId}`);
      if (response.ok) {
        const data = await response.json();
        setCommunity(data);
      }
    } catch (error) {
      console.error('Failed to fetch community:', error);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  // Fetch posts - used for initial load and polling (no auth needed)
  const fetchPosts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  }, [groupId]);

  // Initial load effect
  useEffect(() => {
    fetchCommunity();
    fetchPosts();
    markAsRead();
    
    // Poll for new messages every 3 seconds for real-time feel
    const interval = setInterval(() => {
      fetchPosts();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [groupId, fetchCommunity, fetchPosts, markAsRead]);

  // Removed auto-scroll effect - let users scroll naturally

  const handlePost = async () => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please sign in to post');
      navigate('/login?mode=signup');
      return;
    }
    
    const messageContent = newPost.trim();
    
    // Prevent empty messages
    if (!messageContent) {
      toast.error('Please enter a message');
      return;
    }

    // Prevent double-sending
    if (isSending) return;
    
    setIsSending(true);

    try {
      const response = await fetch(`${API}/communities/${groupId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          content: messageContent,
          anonymous: isAnonymous
        })
      });

      if (response.ok) {
        const newMessage = await response.json();
        
        // Clear input and draft immediately
        setNewPost('');
        localStorage.removeItem(`community_draft_${groupId}`);
        
        // Add new message to posts
        setPosts(prevPosts => [...prevPosts, newMessage]);
        
        toast.success('Posted successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to post message');
      }
    } catch (error) {
      console.error('Failed to post message:', error);
      toast.error('Failed to post message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleLike = async (messageId) => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please sign in to like posts');
      navigate('/login?mode=signup');
      return;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages/${messageId}/like`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });

      if (response.ok) {
        const result = await response.json();
        // Update likes locally
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.message_id === messageId 
              ? { 
                  ...post, 
                  likes: result.likes,
                  liked_by: result.liked 
                    ? [...(post.liked_by || []), user.user_id]
                    : (post.liked_by || []).filter(id => id !== user.user_id)
                }
              : post
          )
        );
      }
    } catch (error) {
      console.error('Failed to like message:', error);
    }
  };

  const handleReply = async (parentId, content, isAnonymousReply = false) => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please sign in to reply');
      navigate('/login?mode=signup');
      return false;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          content: content,
          anonymous: isAnonymousReply,
          parent_id: parentId
        })
      });

      if (response.ok) {
        // Update local state with new reply
        const displayName = isAnonymousReply ? (user.anonymous_name || 'Anonymous') : user.name;
        setPosts(prevPosts => 
          prevPosts.map(post => {
            if (post.message_id === parentId) {
              const newReply = {
                message_id: `temp_${Date.now()}`,
                user_id: user.user_id,
                user_name: displayName,
                user_picture: isAnonymousReply ? '' : user.picture,
                user_avatar: isAnonymousReply ? user.avatar : '',
                user_profile_avatar: isAnonymousReply ? '' : user.profile_avatar,
                content: content,
                anonymous: isAnonymousReply,
                likes: 0,
                liked_by: [],
                created_at: new Date().toISOString()
              };
              return {
                ...post,
                replies_count: (post.replies_count || 0) + 1,
                replies: [...(post.replies || []), newReply]
              };
            }
            return post;
          })
        );
        toast.success('Reply posted!');
        return true;
      } else {
        toast.error('Failed to post reply');
        return false;
      }
    } catch (error) {
      console.error('Failed to post reply:', error);
      toast.error('Failed to post reply');
      return false;
    }
  };

  const handleDelete = async (messageId) => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please sign in to delete');
      navigate('/login?mode=signup');
      return;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages/${messageId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });

      if (response.ok) {
        // Remove message from local state
        setPosts(prevPosts => prevPosts.filter(post => post.message_id !== messageId));
        toast.success('Message deleted!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to delete message');
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleEdit = async (messageId, newContent) => {
    if (!user) {
      toast.error('Please sign in to edit');
      navigate('/login?mode=signup');
      return false;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages/${messageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ content: newContent })
      });

      if (response.ok) {
        const updatedMsg = await response.json();
        setPosts(prevPosts => prevPosts.map(post => 
          post.message_id === messageId ? { ...post, content: updatedMsg.content } : post
        ));
        toast.success('Message updated!');
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to edit message');
        return false;
      }
    } catch (error) {
      console.error('Failed to edit message:', error);
      toast.error('Failed to edit message');
      return false;
    }
  };

  const handleLikeReply = async (parentId, replyId) => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please sign in to like');
      navigate('/login?mode=signup');
      return;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages/${parentId}/replies/${replyId}/like`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });

      if (response.ok) {
        const result = await response.json();
        // Update likes locally on the reply
        setPosts(prevPosts => 
          prevPosts.map(post => {
            if (post.message_id === parentId) {
              return {
                ...post,
                replies: (post.replies || []).map(reply => {
                  if (reply.message_id === replyId) {
                    return {
                      ...reply,
                      likes: result.likes,
                      liked_by: result.liked 
                        ? [...(reply.liked_by || []), user.user_id]
                        : (reply.liked_by || []).filter(id => id !== user.user_id)
                    };
                  }
                  return reply;
                })
              };
            }
            return post;
          })
        );
      }
    } catch (error) {
      console.error('Failed to like reply:', error);
    }
  };

  const handleDeleteReply = async (parentId, replyId) => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please sign in to delete');
      navigate('/login?mode=signup');
      return;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages/${parentId}/replies/${replyId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });

      if (response.ok) {
        // Remove reply from local state (handles nested replies too)
        const removeReplyRecursive = (replies, targetId) => {
          return replies.filter(r => r.message_id !== targetId).map(r => ({
            ...r,
            replies: r.replies ? removeReplyRecursive(r.replies, targetId) : []
          }));
        };
        
        setPosts(prevPosts => 
          prevPosts.map(post => {
            if (post.message_id === parentId) {
              return {
                ...post,
                replies: removeReplyRecursive(post.replies || [], replyId),
                replies_count: Math.max(0, (post.replies_count || 0) - 1)
              };
            }
            // Check nested replies
            return {
              ...post,
              replies: (post.replies || []).map(reply => {
                if (reply.message_id === parentId) {
                  return {
                    ...reply,
                    replies: removeReplyRecursive(reply.replies || [], replyId)
                  };
                }
                return reply;
              })
            };
          })
        );
        toast.success('Reply deleted!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to delete reply');
      }
    } catch (error) {
      console.error('Failed to delete reply:', error);
      toast.error('Failed to delete reply');
    }
  };

  // Handle reply to a reply (nested replies)
  const handleReplyToReply = async (parentReplyId, content, isAnonymousReply) => {
    if (!user) {
      toast.error('Please sign in to reply');
      navigate('/login?mode=signup');
      return false;
    }
    
    try {
      const response = await fetch(`${API}/communities/${groupId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          content: content,
          anonymous: isAnonymousReply,
          parent_id: parentReplyId
        })
      });

      if (response.ok) {
        const newReplyData = await response.json();
        const displayName = isAnonymousReply ? (user.anonymous_name || 'Anonymous') : user.name;
        
        // Add nested reply to the parent reply
        const addNestedReply = (replies, targetId, newReply) => {
          return replies.map(r => {
            if (r.message_id === targetId) {
              return {
                ...r,
                replies: [...(r.replies || []), newReply]
              };
            }
            if (r.replies && r.replies.length > 0) {
              return {
                ...r,
                replies: addNestedReply(r.replies, targetId, newReply)
              };
            }
            return r;
          });
        };
        
        const newReply = {
          message_id: newReplyData.message_id || `temp_${Date.now()}`,
          user_id: user.user_id,
          user_name: displayName,
          user_picture: isAnonymousReply ? '' : user.picture,
          user_avatar: isAnonymousReply ? user.avatar : '',
          user_profile_avatar: isAnonymousReply ? '' : user.profile_avatar,
          content: content,
          anonymous: isAnonymousReply,
          likes: 0,
          liked_by: [],
          replies: [],
          created_at: new Date().toISOString()
        };
        
        setPosts(prevPosts => 
          prevPosts.map(post => ({
            ...post,
            replies: addNestedReply(post.replies || [], parentReplyId, newReply)
          }))
        );
        
        toast.success('Reply posted!');
        return true;
      } else {
        toast.error('Failed to post reply');
        return false;
      }
    } catch (error) {
      console.error('Failed to post nested reply:', error);
      toast.error('Failed to post reply');
      return false;
    }
  };

  const isUserLiked = (post) => {
    if (!user) return false;
    return post.liked_by && post.liked_by.includes(user.user_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!community) {
    return <div className="text-center py-12 text-gray-500">Community not found</div>;
  }

  return (
    <div className="pb-20 md:pb-0">
      {/* Mobile Header */}
      <div className="mb-4 md:hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-indigo-100">
            <Hash className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">{community.name}</h2>
            <p className="text-xs text-gray-500">{community.description}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:grid md:grid-cols-12 gap-4">
        {/* Community Sidebar - Hidden on mobile */}
        <div className="hidden md:block md:col-span-3">
          <div className="bg-gray-50 rounded-xl border border-gray-200/50 p-4 sticky top-24">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-indigo-100">
              <Hash className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-base font-bold text-gray-900 mb-1">{community.name}</h2>
            <p className="text-sm text-gray-600 mb-4">{community.description}</p>
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Messages</span>
                <span className="font-semibold text-gray-900">{posts.filter(p => !p.parent_id).length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Discussion Feed */}
        <div className="md:col-span-9 space-y-3">
          {/* New Post Input - Show sign in prompt for guests */}
          {user ? (
            <div className="bg-gray-50 rounded-xl border border-gray-200/50 p-3">
              <Textarea
                data-testid="message-input"
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="Ask a question..."
                className="w-full min-h-[60px] resize-none border-0 bg-transparent focus-visible:ring-0 text-sm placeholder:text-gray-400 mb-2 p-0"
              />
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <Switch
                    id="anonymous-post"
                    checked={isAnonymous}
                    onCheckedChange={setIsAnonymous}
                    className="data-[state=checked]:bg-indigo-600 scale-90"
                  />
                  <Label htmlFor="anonymous-post" className="text-xs text-gray-600">
                    {isAnonymous ? 'Anonymous' : 'Public'}
                  </Label>
                </div>
                <Button
                  data-testid="post-message-btn"
                  onClick={handlePost}
                  disabled={!newPost.trim() || isSending}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg h-8 px-4 text-xs font-semibold"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Post
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl border border-gray-200/50 p-4 text-center">
              <p className="text-sm text-gray-600 mb-3">Want to start talking?</p>
              <Button
                onClick={() => navigate('/login?mode=signup')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg h-8 px-4 text-xs font-semibold"
              >
                Sign In
              </Button>
            </div>
          )}

          {/* Discussion Posts - Newest first */}
          {(() => {
            const topLevelPosts = posts.filter(post => !post.parent_id);
            return topLevelPosts.length > 0 ? (
              <div className="space-y-3">
                {[...topLevelPosts].reverse().map((post) => (
                  <DiscussionPost 
                    key={post.message_id} 
                    post={post} 
                    onLike={handleLike}
                    userLiked={isUserLiked(post)}
                    onReply={handleReply}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    onLikeReply={handleLikeReply}
                    onDeleteReply={handleDeleteReply}
                    onReplyToReply={handleReplyToReply}
                    user={user}
                    navigate={navigate}
                    groupId={groupId}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200/50">
                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <h3 className="text-base font-semibold text-gray-700 mb-1">No messages yet</h3>
                <p className="text-xs text-gray-500">Start the conversation!</p>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
